import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, NavController, Platform, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { StorageLoader } from './utils/storageLoader';
import { ThemeLoader } from './utils/themeLoader';
import { HardwareBackService } from './core/services/internal/hardware-back.service';
import { PwaService } from './core/services/internal/pwa.service';
import { AppConfigService } from './core/services/internal/app-config.service';
import { ConnectivityService } from './core/services/internal/connectivity.service';
import { SystemAutoRefreshService } from './core/services/internal/system-auto-refresh.service';
import { RouterSeoEffect } from './core/services/internal/router-seo.effect';
import { WebVitalsService } from './core/services/internal/web-vitals.service';
import { SharedEventService } from './core/services/internal/shared_event.service';
import { AuthSessionService } from './core/guards/auth-session.service';
import { PaymentGateService } from './core/services/global/payment-gate.service';
import { OfflineBubbleComponent } from './shared/components/functionals/offline-bubble/offline-bubble.component';
import { SystemRefreshCountdownComponent } from './shared/components/functionals/system-refresh-countdown/system-refresh-countdown.component';
import { CookieConsentComponent } from './shared/components/functionals/cookie-consent/cookie-consent.component';
import { NoAddressModalComponent } from './shared/components/functionals/no-address-modal/no-address-modal.component';
import { CompleteAddressModalComponent } from './shared/components/functionals/complete-address-modal/complete-address-modal.component';
import { CompleteProfileModalComponent } from './shared/components/functionals/complete-profile-modal/complete-profile-modal.component';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    CommonModule,
    IonicModule,
    OfflineBubbleComponent,
    SystemRefreshCountdownComponent,
    CookieConsentComponent,
    NoAddressModalComponent,
    CompleteAddressModalComponent,
    CompleteProfileModalComponent,
  ],
})
export class AppComponent implements OnDestroy {
  isBootstrapping = true;
  private crisisPushSubscription?: Subscription;

  constructor(
    private platform: Platform,
    private navCtrl: NavController,
    private storageLoader: StorageLoader,
    private themeLoader: ThemeLoader,
    private hardwareBackService: HardwareBackService,
    private authSessionService: AuthSessionService,
    private sharedEventService: SharedEventService,
    private toastController: ToastController,
    readonly app: AppConfigService,
    readonly pwa: PwaService,
    readonly connectivity: ConnectivityService,
    readonly paymentGate: PaymentGateService,
    private systemAutoRefresh: SystemAutoRefreshService,
    private routerSeoEffect: RouterSeoEffect,
    private webVitals: WebVitalsService,
  ) {
    this.routerSeoEffect.start();
    this.webVitals.start();
    this.platform.ready().then(() => this.initializeApp());
    this.subscribeToCrisisPush();
  }

  ngOnDestroy() {
    this.crisisPushSubscription?.unsubscribe();
  }

  private subscribeToCrisisPush() {
    this.crisisPushSubscription = this.sharedEventService.taskEvent$.subscribe(
      (event) => {
        if (event?.type === 'crisis_push') {
          void this.showCrisisToast(event.value);
        }
      },
    );
  }

  private async showCrisisToast(payload: any) {
    const data = payload?.data ?? {};
    const title = payload?.title || '⚠️ Crise detectada';
    const body =
      payload?.body || 'Nova crise aguardando atendimento. Toque pra avaliar.';

    const target = this.crisisDeeplinkFor(data);

    const toast = await this.toastController.create({
      header: title,
      message: body,
      color: 'danger',
      position: 'top',
      duration: 12000,
      buttons: target
        ? [
            {
              text: 'Ver crise',
              handler: () => {
                this.navCtrl.navigateForward([target]);
              },
            },
            {
              text: 'Dispensar',
              role: 'cancel',
            },
          ]
        : [{ text: 'OK', role: 'cancel' }],
    });
    await toast.present();
  }

  private crisisDeeplinkFor(data: {
    type?: string;
    crisis_event_id?: string;
    deeplink?: string;
  }): string | null {
    if (data?.deeplink) return data.deeplink;
    if (data?.type === 'crisis_alert' && data.crisis_event_id) {
      return `/dashboard/system/asks/crises/${data.crisis_event_id}`;
    }
    if (data?.type === 'crisis_batch') {
      return '/dashboard/system/asks/crises';
    }
    return null;
  }

  async initializeApp(): Promise<void> {
    try {
      this.app.setDocumentTitle();

      await this.storageLoader.create();
      await this.themeLoader.initialize();
      await this.loadPlatform();

      this.pwa.initialize();
      this.hardwareBackService.initializeBackHandler();

      await this.redirect();
    } finally {
      this.isBootstrapping = false;
    }
  }

  private async loadPlatform(): Promise<void> {
    const existing = await this.storageLoader.getPlatform();

    if (existing) {
      return;
    }

    const detected = this.platform.is('desktop') ? 'desktop' : 'mobile';

    await this.storageLoader.setPlatform(detected);
  }

  private async redirect(): Promise<void> {
    const initialPath =
      typeof window !== 'undefined' ? window.location.pathname : '/';
    const normalized = initialPath.split('?')[0].replace(/\/+$/, '') || '/';

    const isAtRoot = normalized === '/' || normalized === '';
    const guestLandingPaths = ['/home', '/login', '/signup'];
    const isOnGuestLanding = guestLandingPaths.includes(normalized);

    if (!isAtRoot && !isOnGuestLanding) return;

    const authenticated = await this.authSessionService.isAuthenticated();

    if (isAtRoot) {
      if (authenticated) {
        await this.navCtrl.navigateRoot(['/dashboard']);
      }
      return;
    }

    if (isOnGuestLanding && authenticated) {
      await this.navCtrl.navigateRoot(['/dashboard']);
    }
  }

  async installPWA(): Promise<void> {
    const result = await this.pwa.install();

    if (result === 'dismissed') {
      await this.pwa.dismissInstallBanner();
    }
  }

  async dismissPwaBanner(): Promise<void> {
    await this.pwa.dismissInstallBanner();
  }

  async updateApp(): Promise<void> {
    await this.pwa.updateApp();
  }

  dismissUpdateBanner(): void {
    this.pwa.dismissUpdateBanner();
  }
}
